import React, { useEffect, useState } from "react";
import {
    Button,
    Typography,
    TextField,
    Box,
    Tabs,
    Tab,
    Alert,
    Snackbar,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AudioPlayer from "../../../components/AudioPlayer";
import API_ENDPOINTS from "../../../apis/endpoints";
import useAuth from "../../../context";

const emotions = [
    { name: "Neutral", emoji: "😊" },
    { name: "Happy", emoji: "😄" },
    { name: "Angry", emoji: "😡" },
    { name: "Sad", emoji: "😢" },
    { name: "Surprise", emoji: "😲" },
    { name: "Disgust", emoji: "😒" },
    { name: "Fear", emoji: "😨" },
];

function VoiceCloningUpload({ onNext }) {
    const [soundName, setSoundName] = useState("");
    const [selectedEmotion, setSelectedEmotion] = useState(emotions[0].name);
    const [uploadedFiles, setUploadedFiles] = useState(
        emotions.reduce((acc, emotion) => ({ ...acc, [emotion.name]: [] }), {})
    );
    const [validationErrors, setValidationErrors] = useState([]);
    const [showNextButton, setShowNextButton] = useState(false);
    const [showContinueButton, setShowContinueButton] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const auth = useAuth();
    const storedDeviceId = localStorage.getItem("device-id");

    useEffect(() => {
        validateUploads();
    }, [uploadedFiles]);

    const validateUploads = () => {
        const allEmotionsUploaded = emotions.every(
            (emotion) => uploadedFiles[emotion.name].length > 0
        );
        const currentEmotionFull = uploadedFiles[selectedEmotion].length === 50;

        setShowNextButton(!allEmotionsUploaded);
        setShowContinueButton(allEmotionsUploaded);

        if (currentEmotionFull) {
            handleNextEmotion();
        }

        const errors = emotions
            .filter((emotion) => uploadedFiles[emotion.name].length === 0)
            .map((emotion) => emotion.name);
        setValidationErrors(errors);
    };

    const handleNextEmotion = () => {
        const currentIndex = emotions.findIndex(
            (e) => e.name === selectedEmotion
        );
        if (currentIndex < emotions.length - 1) {
            setSelectedEmotion(emotions[currentIndex + 1].name);
        }
    };

    const handleTabChange = (_, newValue) => {
        setSelectedEmotion(newValue);
    };

    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files);
        const validFiles = files.filter(
            (file) =>
                file.type === "audio/mpeg" ||
                file.type === "audio/wav" ||
                file.type === "audio/aac"
        );

        setUploadedFiles((prev) => {
            const updatedFiles = {
                ...prev,
                [selectedEmotion]: [
                    ...prev[selectedEmotion],
                    ...validFiles,
                ].slice(0, 50),
            };

            if (updatedFiles[selectedEmotion].length === 50) {
                setTimeout(() => handleNextEmotion(), 0);
            }

            return updatedFiles;
        });

        event.target.value = null;
    };

    const handleDeleteFile = (fileToDelete) => {
        setUploadedFiles((prev) => ({
            ...prev,
            [selectedEmotion]: prev[selectedEmotion].filter(
                (file) => file !== fileToDelete
            ),
        }));
    };

    const handleContinue = async () => {
        if (!soundName.trim()) {
            setSnackbarOpen(true);
            return;
        }
        if (validationErrors.length === 0) {
            try {
                const response = await fetch(
                    `${API_ENDPOINTS.AI_MODEL_VOICE}`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${auth.accessToken}`,
                            "x-cc-device-id": storedDeviceId,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({ name: soundName }),
                    }
                );

                if (!response.ok) {
                    throw new Error("Failed to create voice model");
                }

                const data = await response.json();

                if (data.success && data.data) {
                    onNext({
                        soundName,
                        folderId: data.data.id,
                        emotions: emotions.map((emotion) => ({
                            name: emotion.name,
                            files: uploadedFiles[emotion.name],
                        })),
                    });
                } else {
                    throw new Error("Invalid response from server");
                }
            } catch (error) {
                console.error("Error creating voice model:", error);
                setSnackbarOpen(true);
            }
        }
    };

    return (
        <div style={{ padding: "10px" }}>
            <Box>
                <Typography variant="highlightSemibold">
                    Sound Name & Upload Sounds
                </Typography>
                <TextField
                    fullWidth
                    value={soundName}
                    onChange={(e) => setSoundName(e.target.value)}
                    placeholder="Enter the name of the sound"
                    margin="normal"
                />

                <Tabs value={selectedEmotion} onChange={handleTabChange}>
                    {emotions.map((emotion) => (
                        <Tab
                            key={emotion.name}
                            label={`${emotion.name} ${emotion.emoji}`}
                            value={emotion.name}
                            sx={{
                                color: validationErrors.includes(emotion.name)
                                    ? "red"
                                    : "inherit",
                            }}
                        />
                    ))}
                </Tabs>

                <Box sx={{ marginTop: "20px" }}>
                    <Typography variant="highlightEmphasis">
                        Emotion: {selectedEmotion}{" "}
                        {
                            emotions.find((e) => e.name === selectedEmotion)
                                ?.emoji
                        }
                    </Typography>
                    <Typography variant="body2">
                        Please upload sound files in{" "}
                        {selectedEmotion.toLowerCase()} emotion up to fifty
                        files.
                    </Typography>

                    <Box
                        sx={{
                            border: "2px dashed grey",
                            borderRadius: "8px",
                            padding: "20px",
                            textAlign: "center",
                            marginTop: "10px",
                        }}
                    >
                        <input
                            accept="audio/mpeg,audio/wav,audio/aac"
                            id={`upload-${selectedEmotion}`}
                            type="file"
                            multiple
                            style={{ display: "none" }}
                            onChange={handleFileUpload}
                        />
                        <label htmlFor={`upload-${selectedEmotion}`}>
                            <Button
                                variant="contained"
                                component="span"
                                startIcon={<CloudUploadIcon />}
                            >
                                Upload or Drag & Drop Sounds
                            </Button>
                        </label>
                        <Typography variant="body2">
                            Accepted formats: .mp3, .wav, .aac
                        </Typography>
                    </Box>

                    {uploadedFiles[selectedEmotion].length > 0 && (
                        <Box sx={{ marginTop: "20px" }}>
                            <Typography variant="h6">
                                Uploaded Files:
                            </Typography>
                            {uploadedFiles[selectedEmotion].map(
                                (file, index) => (
                                    <AudioPlayer
                                        key={index}
                                        file={file}
                                        onDelete={() => handleDeleteFile(file)}
                                    />
                                )
                            )}
                        </Box>
                    )}
                </Box>

                {validationErrors.length > 0 && (
                    <Alert
                        severity="warning"
                        sx={{ width: "fit-content", my: 2 }}
                    >
                        Please upload at least one sound for each emotion before
                        proceeding.
                    </Alert>
                )}

                {showNextButton && (
                    <Button
                        variant="contained"
                        sx={{
                            width: "25%",
                            marginTop: "3rem",
                            marginBottom: "2rem",
                        }}
                        onClick={handleNextEmotion}
                    >
                        Next
                    </Button>
                )}

                {showContinueButton && (
                    <Button
                        variant="contained"
                        sx={{
                            width: "25%",
                            marginTop: "3rem",
                            marginBottom: "2rem",
                        }}
                        onClick={handleContinue}
                    >
                        Continue
                    </Button>
                )}
            </Box>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message="Please enter a sound name before continuing"
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={snackbarOpen}
                    severity="error"
                    sx={{ width: "100%" }}
                >
                    Please enter a sound name before continuing{" "}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default VoiceCloningUpload;
